import React, { Component } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { analyticsQuery } from '../graphql/analytics.query';
import { Loading } from '../../common/components/loading.component';
import { DatePicker, RaisedButton } from 'material-ui';
import { Theme } from '../../theme/theme';

const METRIC_GROUPS = [
  { label: 'App & Authentication', categories: ['App', 'Auth'] },
  { label: 'Video & Manuals', categories: ['Video', 'Manual'] },
  { label: 'Homepage', categories: ['Homepage'] },
  { label: 'Feed & Events', categories: ['Feed', 'Feed-EventView-NewsFeedEventItem', 'Feed-EventView-NewsFeedItemType', 'Feed-NewsReadMore-NewsFeedNewsItem'] },
  { label: 'Yoga Features', categories: ['Feature'] },
];

function groupParams(params) {
  const assigned = new Set();

  const groups = METRIC_GROUPS.map((group) => {
    const items = params.filter((param) => {
      if (!group.categories.includes(param.category)) return false;
      const key = `${param.category}/${param.action}/${param.identifier}`;
      if (assigned.has(key)) return false;
      assigned.add(key);
      return true;
    });
    return { label: group.label, items };
  }).filter((group) => group.items.length > 0);

  const ungrouped = params.filter((param) => {
    const key = `${param.category}/${param.action}/${param.identifier}`;
    return !assigned.has(key);
  });

  if (ungrouped.length > 0) {
    groups.push({ label: 'Other', items: ungrouped });
  }

  return groups;
}

function findMetricValue(data, param) {
  return data.find(
    (item) =>
      item.category === param.category &&
      item.action === param.action &&
      item.identifier === param.identifier,
  ) || { value: 0 };
}

export class AnalyticsView extends Component {
  static propTypes = {
    startTime: PropTypes.any,
    endTime: PropTypes.any,
    client: PropTypes.any,
    params: PropTypes.any,
    onDateChange: PropTypes.func,
  };

  constructor() {
    super();
    this.state = {
      startTime: moment().subtract(7, 'days').startOf('day'),
      endTime: moment().endOf('day'),
      loading: true,
      data: [],
    };
  }

  componentWillMount() {
    if (this.props.startTime && this.props.endTime) {
      this.setState(
        { startTime: this.props.startTime, endTime: this.props.endTime },
        () => this.reloadData(),
      );
    } else {
      this.reloadData();
    }
  }

  reloadData() {
    this.setState({ loading: true });
    const { startTime, endTime } = this.state;
    const { params, onDateChange, client } = this.props;

    if (onDateChange) {
      onDateChange(startTime, endTime);
    }

    client
      .query({
        query: analyticsQuery,
        fetchPolicy: 'network-only',
        variables: {
          startTime: startTime.utc().format(),
          endTime: endTime.utc().format(),
          params: params.map((item) => ({
            category: item.category,
            action: item.action,
            identifier: item.identifier || null,
            flags: item.flags || {},
          })),
        },
      })
      .then(({ data }) => {
        this.setState({ data: data.analytics, loading: false });
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  }

  handleStartDateChange = (event, startDate) => {
    const newStartTime = moment(startDate).startOf('day');
    this.setState({ startTime: newStartTime }, () => this.reloadData());
  };

  handleEndDateChange = (event, endDate) => {
    const newEndTime = moment(endDate).endOf('day');
    this.setState({ endTime: newEndTime }, () => this.reloadData());
  };

  renderMetric(param, index) {
    const resultData = findMetricValue(this.state.data, param);

    return (
      <div
        key={index}
        className="rounded-md border border-gray-100 bg-gray-50 px-3 py-3 text-center"
      >
        <div className="text-xs font-medium text-gray-600">{param.name}</div>
        <div
          className="mt-1 text-2xl font-semibold tabular-nums"
          style={{ color: Theme.SECONDARY_TEXT_COLOR }}
        >
          {resultData.value}
          {param.flags && param.flags.unique && (
            <span className="ml-1 text-xs font-normal text-gray-400">users</span>
          )}
        </div>
      </div>
    );
  }

  render() {
    if (this.state.loading) {
      return <Loading small />;
    }

    const groups = groupParams(this.props.params);

    return (
      <div className="space-y-5">
        <div className="flex flex-wrap items-end gap-3 rounded-md border border-gray-100 bg-gray-50 p-3">
          <DatePicker
            firstDayOfWeek={0}
            autoOk
            floatingLabelText="Start date"
            style={{ width: 160 }}
            value={this.state.startTime.toDate()}
            onChange={this.handleStartDateChange}
            container="inline"
          />
          <DatePicker
            firstDayOfWeek={0}
            autoOk
            floatingLabelText="End date"
            style={{ width: 160 }}
            value={this.state.endTime.toDate()}
            onChange={this.handleEndDateChange}
            container="inline"
          />
          <RaisedButton
            onClick={() => this.reloadData()}
            label="Search"
            backgroundColor={Theme.PRIMARY_COLOR}
            labelColor="#fff"
          />
        </div>

        {groups.map((group) => (
          <div key={group.label}>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
              {group.label}
            </h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {group.items.map((param, index) => this.renderMetric(param, index))}
            </div>
          </div>
        ))}
      </div>
    );
  }
}
